import { useGlobalStore } from '../stores/globalStore';

// Security levels: 0 = No Access, 1 = View Only, 2 = Add/Edit, 3 = Full (Delete), 6 = User Management (Keycloak accounts)
export const LEVEL = { NONE: 0, VIEW: 1, EDIT: 2, FULL: 3, USER_MGMT: 6 } as const;

export function usePermission() {
  const role = useGlobalStore((s) => s.userRole);

  const getLevel = (field: string): number => {
    // No role = no access. This used to return 99 ("allow all") so buttons rendered during the
    // role fetch, but a failed fetch left it that way for the whole session: every route guard and
    // in-page gate stayed open. Pop-in is handled instead by MainLayout, which holds the page until
    // the role has loaded (or shows the error when it cannot be read).
    if (!role) return LEVEL.NONE;
    const val = (role as unknown as Record<string, number | null>)[field];
    return val ?? LEVEL.NONE;
  };

  return {
    // Main levels
    facilityLevel: getLevel('dietech_Facility_Level'),
    residentLevel: getLevel('dietech_Resident_Level'),
    menuCycleLevel: getLevel('dietech_Menu_Cycle_Level'),
    tableLevel: getLevel('dietech_Table_Level'),
    /** Items: 1 view, 2 modify, 3 add, 4 delete (ucDietech_Items.vb:344-365). */
    itemsLevel: getLevel('dietech_Items_Level'),
    reportLevel: getLevel('dietech_Report_Level'),
    recipeLevel: getLevel('dietech_Recipe_Level'),
    selectiveMenuLevel: getLevel('dietech_Selective_Menu_Level'),
    creditLevel: getLevel('dietech_Credit_Level'),
    locationLevel: getLevel('dietech_Location_Level'),
    consistencyLevel: getLevel('dietech_Consistency_Level'),
    dietTypeLevel: getLevel('dietech_Diet_Type_Level'),
    nutritionalAssessmentLevel: getLevel('dietech_Nutritional_Assessment_Level'),
    billingCodeLevel: getLevel('dietech_Billing_Code_Table_Level'),
    trayLineLevel: getLevel('dietech_Tray_Line_Level'),
    userLevel: getLevel('user_Level'),
    rolesLevel: getLevel('roles_Level'),
    dietechLevel: getLevel('dietech_Level'),
    roomServiceConfigLevel: getLevel('dietech_Room_Service_Config_Level'),
    kitchenDisplayLevel: getLevel('dietech_Kitchen_Display_Level'),

    /**
     * False until the user's role has actually loaded (a user with no role gets NO_ROLE, which
     * counts as loaded). Every level reads 0 before then; this lets a screen say "loading" rather
     * than "no access".
     */
    roleLoaded: !!role,

    // Helpers
    canView: (field: string) => getLevel(field) >= LEVEL.VIEW,
    canEdit: (field: string) => getLevel(field) >= LEVEL.EDIT,
    canDelete: (field: string) => getLevel(field) >= LEVEL.FULL,
    getLevel,
  };
}
